import { computed, reactive, ref, watch, type Ref } from 'vue'
import { SubAppColourVariables } from '@/types/sub-app-types'

const colourFallbacks: Record<string, string> = {
  '--colours-status-bar': '--colours-app-bar',
  '--colours-app-bar-text': '--colours-grey-1',
  '--colours-menu-bar-text': '--colours-grey-1',
}

function defaultColour(css: Record<string, string>, key: string) {
  const fallback = colourFallbacks[key]
  if (fallback && css[fallback]) return css[fallback]

  const root = getComputedStyle(document.documentElement)
  const value = root.getPropertyValue(key)
  if (value === '' && fallback) return root.getPropertyValue(fallback)

  return value
}

export function withDefaultColours(css: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    SubAppColourVariables.map(colour => [
      colour.css,
      css[colour.css] ?? defaultColour(css, colour.css),
    ])
  )
}

export function useSubAppColours(
  cssVariables: Ref<Record<string, string>>,
  savedCssVariables: Record<string, string>
) {
  const colours = ref<{ label: string; css: string; value: string }[]>([])

  const subAppColourGroupKeys: { title: string; keys: string[] }[] = [
    { title: 'Primary', keys: ['--colours-primary', '--colours-primary-text'] },
    { title: 'Secondary', keys: ['--colours-secondary', '--colours-secondary-text'] },
    {
      title: 'App Bar',
      keys: [
        '--colours-app-bar',
        '--colours-app-bar-text',
        '--colours-menu-bar',
        '--colours-menu-bar-text',
        '--colours-status-bar',
      ],
    },
  ]

  const colourGroups = computed(() =>
    subAppColourGroupKeys.map(group => ({
      title: group.title,
      colours: group.keys
        .map(key => colours.value.find(colour => colour.css === key))
        .filter((colour): colour is (typeof colours.value)[number] => !!colour),
    }))
  )

  const openGroups = reactive<Record<string, boolean>>(
    Object.fromEntries(subAppColourGroupKeys.map(group => [group.title, false]))
  )

  function toColours(css: Record<string, string>) {
    const values = withDefaultColours(css)
    return SubAppColourVariables.map(colour => ({ ...colour, value: values[colour.css]! }))
  }

  function resetColours() {
    colours.value = toColours(savedCssVariables)
  }

  watch(
    cssVariables,
    css => {
      const next = toColours(css)
      if (JSON.stringify(next) !== JSON.stringify(colours.value)) {
        colours.value = next
      }
    },
    { immediate: true }
  )

  watch(
    colours,
    current => {
      const next = Object.fromEntries(current.map(colour => [colour.css, colour.value]))
      if (JSON.stringify(next) !== JSON.stringify(cssVariables.value)) {
        cssVariables.value = next
      }
    },
    { deep: true }
  )

  return { colours, colourGroups, openGroups, resetColours }
}
